import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, LogOut, ArrowLeft, Wallet, CheckCircle } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const corridors = [
  { from: 'US', to: 'Mexico', fromCurrency: 'USD', toCurrency: 'MXN' },
  { from: 'US', to: 'Philippines', fromCurrency: 'USD', toCurrency: 'PHP' },
  { from: 'EU', to: 'Nigeria', fromCurrency: 'EUR', toCurrency: 'NGN' }
];

const CryptoPaymentPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { account, isConnected, connectWallet, connecting, sendUSDCPayment } = useWallet();
  
  const [formData, setFormData] = useState({
    amount: '',
    corridor: 0,
    recipient_name: '',
    recipient_account: '',
    token: 'USDC'
  });
  const [processing, setProcessing] = useState(false);
  const [txHash, setTxHash] = useState(null);

  const selectedCorridor = corridors[formData.corridor];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!formData.recipient_name || !formData.recipient_account) {
      toast.error('Please fill in all recipient details');
      return;
    }

    setProcessing(true);
    
    try {
      const recipientAddress = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb';
      
      const result = await sendUSDCPayment(amount, recipientAddress, formData.token);
      
      if (result) {
        setTxHash(result.hash);
        
        const transactionData = {
          amount: amount,
          from_currency: selectedCorridor.fromCurrency,
          to_currency: selectedCorridor.toCurrency,
          from_country: selectedCorridor.from,
          to_country: selectedCorridor.to,
          recipient_name: formData.recipient_name,
          recipient_account: formData.recipient_account
        };

        await axios.post(`${API}/transactions?user_id=${user.id}`, transactionData);
        
        toast.success('Transfer completed successfully!');
        setTimeout(() => navigate('/transactions'), 2000);
      }
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error('Transfer failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')} data-testid='back-to-dashboard-btn'>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-2'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout} data-testid='logout-btn'>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className='max-w-4xl mx-auto px-4 py-8'>
        <div className='mb-6'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>Crypto Payment</h1>
          <p className='text-gray-600'>Send money using USDC or USDT directly from your wallet</p>
        </div>

        {!isConnected ? (
          <Card className='border-0 shadow-lg mb-6' data-testid='wallet-connect-card'>
            <CardContent className='p-8 text-center'>
              <div className='w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Wallet className='w-10 h-10 text-white' />
              </div>
              <h2 className='text-2xl font-bold mb-2'>Connect Your Wallet</h2>
              <p className='text-gray-600 mb-6'>Connect your MetaMask wallet to send crypto payments</p>
              <Button 
                onClick={connectWallet} 
                disabled={connecting}
                className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                data-testid='connect-wallet-btn'
              >
                {connecting ? 'Connecting...' : 'Connect MetaMask'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className='border-0 shadow-lg mb-6' data-testid='wallet-info-card'>
              <CardContent className='p-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm text-gray-600 mb-1'>Connected Wallet</p>
                    <p className='font-mono text-sm' data-testid='connected-address'>{account}</p>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <CheckCircle className='w-5 h-5 text-green-500' />
                    <span className='text-green-600 font-semibold'>Connected</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className='grid md:grid-cols-2 gap-6'>
              <Card className='border-0 shadow-lg' data-testid='payment-form-card'>
                <CardHeader>
                  <CardTitle>Transfer Details</CardTitle>
                  <CardDescription>Enter payment information</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className='space-y-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='corridor'>Transfer Corridor</Label>
                      <Select value={formData.corridor.toString()} onValueChange={(value) => setFormData({ ...formData, corridor: parseInt(value) })}>
                        <SelectTrigger data-testid='corridor-select'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {corridors.map((c, index) => (
                            <SelectItem key={index} value={index.toString()}>
                              {c.from} → {c.to}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='token'>Stablecoin</Label>
                      <Select value={formData.token} onValueChange={(value) => setFormData({ ...formData, token: value })}>
                        <SelectTrigger data-testid='token-select'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='USDC'>USDC</SelectItem>
                          <SelectItem value='USDT'>USDT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='amount'>Amount ({formData.token})</Label>
                      <Input
                        id='amount'
                        type='number'
                        step='0.01'
                        placeholder='500'
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        data-testid='amount-input'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='recipient_name'>Recipient Name</Label>
                      <Input
                        id='recipient_name'
                        placeholder='John Doe'
                        value={formData.recipient_name}
                        onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                        data-testid='recipient-name-input'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='recipient_account'>Recipient Account</Label>
                      <Input
                        id='recipient_account'
                        placeholder='Bank account or mobile money number'
                        value={formData.recipient_account}
                        onChange={(e) => setFormData({ ...formData, recipient_account: e.target.value })}
                        data-testid='recipient-account-input'
                      />
                    </div>

                    <Button 
                      type='submit' 
                      className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' 
                      disabled={processing}
                      data-testid='submit-payment-btn'
                    >
                      {processing ? 'Processing...' : 'Send Payment'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className='space-y-4'>
                <Card className='border-0 shadow-lg' data-testid='fee-breakdown-card'>
                  <CardHeader>
                    <CardTitle>Fee Breakdown</CardTitle>
                    <CardDescription>Transparent pricing</CardDescription>
                  </CardHeader>
                  <CardContent className='space-y-3'>
                    {formData.amount && (
                      <>
                        <div className='flex justify-between'>
                          <span className='text-gray-600'>Amount</span>
                          <span className='font-semibold'>${parseFloat(formData.amount || 0).toFixed(2)}</span>
                        </div>
                        <div className='flex justify-between'>
                          <span className='text-gray-600'>Service Fee (0.48%)</span>
                          <span className='font-semibold'>${(parseFloat(formData.amount || 0) * 0.0048).toFixed(2)}</span>
                        </div>
                        <div className='flex justify-between'>
                          <span className='text-gray-600'>Network Fee (est.)</span>
                          <span className='font-semibold'>$0.50</span>
                        </div>
                        <div className='border-t pt-3 flex justify-between'>
                          <span className='font-bold'>Recipient Gets</span>
                          <span className='font-bold text-emerald-600'>
                            ${(parseFloat(formData.amount || 0) - (parseFloat(formData.amount || 0) * 0.0048) - 0.50).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {txHash && (
                  <Card className='border-0 shadow-lg bg-green-50' data-testid='success-card'>
                    <CardContent className='p-6'>
                      <div className='flex items-start space-x-3'>
                        <CheckCircle className='w-6 h-6 text-green-500 flex-shrink-0 mt-1' />
                        <div>
                          <p className='font-semibold text-green-900 mb-2'>Payment Successful!</p>
                          <p className='text-sm text-green-700 mb-2'>Transaction Hash:</p>
                          <p className='text-xs font-mono text-green-600 break-all'>{txHash}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CryptoPaymentPage;